import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { SubscribeContactsApiArg } from '@/__generated/es-api'
import { ERROR_SUBSCRIBE_TO_CAMPAIGN_SERVER } from '@/app/module/campaigns/definitions'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

/**
 * Subscribe multiple contacts to a campaign
 */
export const useSubscribeContacts = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [subscribeContacts, res] = esApi.endpoints.subscribeContacts.useMutation()

  const subscribeContactsToCampaign = useCallback(
    async (campaignId: number, body: SubscribeContactsApiArg['body']) => {
      try {
        const result = await subscribeContacts({
          orgId: orgId.toString(),
          campaignId: campaignId.toString(),
          body: {
            allowResubscribe: true,
            args: body.args,
            contacts: body.contacts,
            delay: body.delay,
            startUntil: body.startUntil,
            timeZone: body.timeZone,
          },
        }).unwrap()

        dispatch(
          createNotification({
            'campaign-subscribe-contacts': {
              type: 'success',
              message: `${result.total || 0} contacts successfully subscribed to campaign.`,
            },
          }),
        )
        return result
      } catch (error) {
        dispatch(
          createNotification({
            'campaign-subscribe-contacts-failure': {
              type: 'error',
              message: getErrorMessage(error, ERROR_SUBSCRIBE_TO_CAMPAIGN_SERVER),
            },
          }),
        )
        return undefined
      }
    },
    [orgId, subscribeContacts, dispatch],
  )

  return [subscribeContactsToCampaign, res] as const
}
